import './popupInvioReport.scss';

import React, { useState, useImperativeHandle, useEffect, useCallback } from 'react';
import { TextBox, TagBox, Button, Popup, Toast, LoadPanel } from 'devextreme-react';
import { Position, ToolbarItem } from 'devextreme-react/popup';
import { useScreenSize, useScreenSizeClass } from '../../../../utils/media-query';
import { ToastType } from 'devextreme/ui/toast';
import { ClickEvent } from 'devextreme/ui/button';

import useApiConfiguration from '../../../../apiConfig';
import { RapportiniApi } from '../../../../data';

export type PopupInvioReportHandle = {
    show: (id: number, codiceCliente: string) => void;
};

type State = {
    id: number;
    codiceCliente: string;
    visibile: boolean;
};

const PopupInvioReport: React.ForwardRefRenderFunction<PopupInvioReportHandle> = (props, ref) => {
    const [state, setState] = useState<State>({ id: undefined, codiceCliente: '', visibile: false });
    const [toastConfig, setToastConfig] = useState({ message: '', type: 'error', visible: false } as { message: string, type: ToastType, visible: boolean });
    const apiConfig = useApiConfiguration();
    const { isSmall, isXSmall } = useScreenSize();
    const screenSizeClass = useScreenSizeClass();

    const [loading, setLoading] = useState<boolean>(false);
    const [dataSource, setDataSource] = useState<string[]>([]);
    const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState<string>('');

    useImperativeHandle(ref, () => ({
        show: (id: number, codiceCliente: string) => {
            setLoading(false);
            setState({ id, codiceCliente, visibile: true });
        }
    }));

    useEffect(() => {
        if (state.visibile) {
            setSelectedEmails([]);
            setNewEmail('');

            const api = new RapportiniApi(apiConfig);
            api.getEmailsCliente({ codiceCliente: state.codiceCliente })
                .then(res => {
                    setDataSource(res);
                }).finally(() => setLoading(false));
        }
    }, [state.visibile, state.id, state.codiceCliente]);

    const addEmail = () => {
        const email = newEmail.trim();
        if (email && !selectedEmails.includes(email))
            setSelectedEmails(prev => [...prev, email]);

        setNewEmail('');
    };

    const removeEmail = (email: string) => {
        setSelectedEmails(prev => prev.filter(e => e !== email));
    };

    const inviaRapportino = (e: ClickEvent) => {
        setLoading(true);

        const api = new RapportiniApi(apiConfig);
        api.invioRapportino({
            rapportinoInvioDTO: {
                id: state.id,
                destinatari: selectedEmails
            }
        }).then((res) => {
            setToastConfig({
                visible: true,
                type: 'success',
                message: 'Rapportino inviato con successo.'
            });

            hideInfo();
        }).catch((error) => {
            console.error(error);
            setToastConfig({
                visible: true,
                type: 'error',
                message: 'Errore durante l\'invio del rapportino.'
            });
        }).finally(() => setLoading(false));
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            visible: false
        });
    }, [toastConfig, setToastConfig]);

    function hideInfo() {
        setState({ ...state, visibile: false });
    };

    return (
        <React.Fragment>
            <Popup
                visible={state.visibile} showTitle showCloseButton dragEnabled={false}
                fullScreen={(isSmall || isXSmall)} width={650} height={400}
                hideOnOutsideClick onHidden={hideInfo} title='Invia Rapportino'
                container=".dx-viewport"
            >
                {loading && <LoadPanel style={{ zIndex: 9999 }} position={'center'} visible shading shadingColor='rgba(0, 0, 0, 0.4)' />}

                <ToolbarItem widget='dxButton' location='center' toolbar='bottom' cssClass='popupInvioReport toolbar'>
                    <Button
                        icon="custom-forward material-symbols-outlined"
                        text='Invia'
                        type='default'
                        stylingMode='contained'
                        onClick={inviaRapportino}
                    />
                </ToolbarItem>

                <Position at="center" my="center" of="" collision="fit" />

                <div className={`popup-invio-container ${screenSizeClass}`}>
                    {/* 🔹 Colonna sinistra */}
                    <div className="input-section">
                        <TagBox
                            dataSource={dataSource}
                            value={selectedEmails}
                            placeholder='Seleziona destinatari'
                            maxDisplayedTags={0}
                            showSelectionControls searchEnabled
                            applyValueMode='instantly'
                            onValueChanged={(e) => setSelectedEmails(e.value)}
                        />

                        <div className="manual-input">
                            <TextBox
                                label="Aggiungi Email"
                                labelMode="floating"
                                value={newEmail}
                                onValueChanged={(e) => setNewEmail(e.value)}
                                onEnterKey={addEmail}
                                className="email-textbox"
                            />
                            <Button
                                icon="add"
                                type="default"
                                stylingMode="text"
                                onClick={addEmail}
                                disabled={!newEmail.trim()}
                                className="add-button"
                            />
                        </div>
                    </div>

                    {/* 🔹 Colonna destra */}
                    <div className="selected-section">
                        <h4>Email selezionate</h4>
                        {selectedEmails.length === 0 ? (
                            <div className="empty-text">Nessuna email selezionata</div>
                        ) : (
                            <div className="tag-list">
                                {selectedEmails.map((email, idx) => (
                                    <div key={idx} className="email-tag">
                                        <span className="tag-text">{email}</span>
                                        <Button
                                            icon="close"
                                            hint="Rimuovi"
                                            stylingMode="text"
                                            onClick={() => removeEmail(email)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Popup>

            <Toast 
                visible={toastConfig.visible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type={toastConfig.type} displayTime={3000}
                onHiding={onHidingToast}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />
        </React.Fragment>
    );
};

export default React.forwardRef(PopupInvioReport);